// pages/Process.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaSearch, 
  FaPenAlt, 
  FaCode, 
  FaCheckCircle, 
  FaRocket, 
  FaCogs 
} from 'react-icons/fa';
import './Process.css';

const processSteps = [
    {
        id: 1,
        icon: <FaSearch />,
        title: 'Discovery & Analysis',
        description: 'We conduct in-depth analysis of your business requirements, target audience, and competitive landscape to create a strategic project roadmap.',
        duration: '1-2 Weeks',
        deliverables: ['Project Requirements Document', 'Technical Architecture', 'Sitemap', 'Competitive Analysis'],
        color: '#2563eb'
    },
    {
        id: 2,
        icon: <FaPenAlt />,
        title: 'Strategy & Design',
        description: 'Our UX/UI experts create wireframes, prototypes, and visual designs focused on user experience and conversion optimization.',
        duration: '2-3 Weeks',
        deliverables: ['Wireframes', 'UI Mockups', 'Design System', 'Interactive Prototypes'],
        color: '#7c3aed'
    },
    {
        id: 3,
        icon: <FaCode />,
        title: 'Development',
        description: 'Our certified developers implement the design using modern frameworks and best practices for performance and scalability.',
        duration: '3-6 Weeks',
        deliverables: ['Frontend Development', 'Backend Development', 'API Integration', 'Third-party Services'],
        color: '#f59e0b'
    },
    {
        id: 4,
        icon: <FaCheckCircle />,
        title: 'Testing & QA',
        description: 'Comprehensive testing including functionality, performance, security, and cross-browser compatibility.',
        duration: '1-2 Weeks',
        deliverables: ['Functional Testing', 'Performance Testing', 'Security Audit', 'Cross-browser Testing'],
        color: '#10b981'
    },
    {
        id: 5,
        icon: <FaRocket />,
        title: 'Deployment',
        description: 'Professional deployment with proper configuration, monitoring setup, and launch support.',
        duration: '1 Week',
        deliverables: ['Production Deployment', 'SSL Setup', 'CDN Configuration', 'Launch Checklist'],
        color: '#3b82f6'
    },
    {
        id: 6,
        icon: <FaCogs />,
        title: 'Maintenance & Support',
        description: 'Ongoing support, updates, and optimization to ensure your website performs at its best.',
        duration: 'Ongoing',
        deliverables: ['Security Updates', 'Performance Monitoring', 'Content Updates', 'Technical Support'],
        color: '#6b7280'
    }
];

const Process = () => {
  const navigate = useNavigate();
    return (
        <section id="process" className="section process">
            <div className="animated-bg">
                <div className="bg-grid"></div>
            </div>
            
            <div className="container">
                <div className="section-header center">
                    <div className="section-tag">Our Methodology</div>
                    <h2 className="section-title">Our Proven Development Process</h2>
                    <p className="section-subtitle">
                        A structured, transparent approach to delivering exceptional web solutions that drive business growth
                    </p>
                </div>

                {/* Vertical Timeline with Alternating Sides */}
                <div className="process-timeline">
                    {processSteps.map((step, index) => (
                        <div 
                            key={step.id} 
                            className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                        >
                            <div className="timeline-content">
                                <div className="timeline-icon" style={{ backgroundColor: step.color }}>
                                    {step.icon}
                                    <div className="step-number">{index + 1}</div>
                                </div>
                                
                                <div className="timeline-card">
                                    <div className="timeline-header">
                                        <h3>{step.title}</h3>
                                        <span className="duration">{step.duration}</span>
                                    </div>
                                    
                                    <p className="timeline-description">{step.description}</p>
                                    
                                    <div className="deliverables">
                                        <h4>Key Deliverables:</h4>
                                        <ul>
                                            {step.deliverables.map((item, i) => (
                                                <li key={i}>
                                                    <span className="check">✓</span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {/* Timeline Line */}
                    <div className="timeline-line"></div>
                </div>

                {/* Process Statistics */}
                <div className="process-stats">
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-number">98%</div>
                            <div className="stat-label">On-time Delivery</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-number">250+</div>
                            <div className="stat-label">Projects Completed</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-number">6</div>
                            <div className="stat-label">Structured Phases</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-number">24/7</div>
                            <div className="stat-label">Support Available</div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="process-cta">
                    <div className="cta-content">
                        <h3>Ready to Transform Your Digital Presence?</h3>
                        <p>Schedule a free consultation with our experts to discuss your project requirements</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/contact')}
                        >
                            Schedule Free Consultation
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Process;